import { setHours, setMinutes } from 'date-fns';

import { Separator } from '@components/separator';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { abbreviateDate } from '@utils/date';
import { hourOptions, minuteOptions } from '@utils/hoursOptions';

import { useJourneyAction, useUpdateJourneyAction } from 'api/journey-actions/hooks';
import { BASE_ACTION_TYPE, JOURNEY_ACTION_TYPE } from 'api/types';
import { ACTION_TYPES } from 'ui/actions/action-config';
import { useNodes } from 'ui/journey-builder/hooks/use-nodes';
import {
  ActionFooter,
  ActionHeader,
  ActionSidebarLoading,
  SidebarProps,
  createActionForm,
  useCreateActionFormStore,
} from 'ui/journey-builder/node-types/common';
import { showActionSidebar } from 'ui/journey-builder/show-action-sidebar';

import { FormWithUnsavedChangesPrompt } from '../../journey-builder/unsaved-changes-prompt';
import { ActionTimeSelector, WaitForDateStateGroup } from '../common';
import { ActionTimeWithTimezoneTooltip, newDateIgnoreLocalTimezone } from '../timezone';
const ACTION_TYPE = ACTION_TYPES.wait_until_fixed_date;

type WaitUntilDateData = {
  date: Date;
  hour: string;
  minute: string;
};

const Form = createActionForm<WaitUntilDateData>();

export const SidebarContent = (props: SidebarProps) => {
  const { journeyActionId } = props;
  const { data, isLoading } = useJourneyAction<typeof ACTION_TYPE>(journeyActionId);
  const { updateSelectedNode } = useNodes();
  const { t } = useScopedTranslate(sidebarLocale);
  const formStore = useCreateActionFormStore();
  const isDirty = formStore.useIsDirty();
  const date = formStore.useFieldValue('date') as Date;
  const hour = formStore.useFieldValue('hour') as string;
  const minute = formStore.useFieldValue('minute') as string;
  const defaultHour = 9;
  const defaultMinute = 0;

  const values = data?.values;
  const dateString = date
    ? t('selectDateInfo', {
        date: abbreviateDate(date, 'en'),
        hour:
          hourOptions.find((option) => option.value === hour)?.label ??
          `0${defaultHour.toString()}`,
        minute:
          minuteOptions.find((option) => option.value === minute)?.label ??
          `0${defaultMinute.toString()}`,
      })
    : t('noDateSelected');
  const initialDate = values?.date?.value
    ? newDateIgnoreLocalTimezone(values.date.value)
    : setHours(setMinutes(new Date(), defaultMinute), defaultHour);

  const editMutation = useUpdateJourneyAction(journeyActionId);

  const handleSave = async (data: WaitUntilDateData) => {
    const dateObject = new Date(
      Date.UTC(
        data.date.getFullYear(),
        data.date.getMonth(),
        data.date.getDate(),
        Number(data.hour),
        Number(data.minute)
      )
    );

    await editMutation.mutateAsync({
      isAutomatic: true,
      action: ACTION_TYPE,
      values: {
        date: { value: dateObject.toISOString() },
      },
      description: null,
    });
  };

  if (isLoading || !data) {
    return <ActionSidebarLoading actionType={ACTION_TYPE} {...props} />;
  }

  const [offset] = data?.timezone?.split(' ') ?? [''];

  return (
    <Sidebar>
      <ActionHeader actionType={ACTION_TYPE} {...props} />
      <Sidebar.Content>
        <FormWithUnsavedChangesPrompt store={formStore} onSubmit={(data) => handleSave(data)}>
          <Stack space={4}>
            <Stack space={8}>
              <Stack space={4} orientation="vertical">
                <WaitForDateStateGroup
                  selectedStateId="wait_until_fixed_date"
                  onStateSelected={(state) => {
                    formStore.actions.resetStore();
                    window.requestAnimationFrame(() => {
                      showActionSidebar({
                        type: state.id as JOURNEY_ACTION_TYPE,
                        id: journeyActionId,
                      });
                      updateSelectedNode({ type: state.id as BASE_ACTION_TYPE });
                    });
                  }}
                />
              </Stack>
              <Text font="regular14" text={t('configurationDate')} />
              <Separator />
              <Stack space={4}>
                <Form.DatePicker
                  defaultValue={values?.date?.value ? initialDate : ''}
                  id="date"
                  label={t('date')}
                  placeholder="DD / MM / YYYY"
                  validators={['required']}
                />
                <ActionTimeSelector
                  defaultHour={initialDate.getHours().toString()}
                  defaultMinute={initialDate.getMinutes().toString()}
                />
                <ActionTimeWithTimezoneTooltip
                  actionText={dateString}
                  hour={hour}
                  minute={minute}
                  journeyTimezoneOffset={offset}
                />
              </Stack>
            </Stack>
            <ActionFooter
              action={data}
              isDisabled={!isDirty || editMutation.isLoading}
              form={Form}
            />
          </Stack>
        </FormWithUnsavedChangesPrompt>
      </Sidebar.Content>
    </Sidebar>
  );
};

const sidebarLocale = {
  en: {
    configurationDate: 'Triggers the following action on select date',
    selectDateInfo: 'Triggers on • {date} at {hour}:{minute}',
    date: '* Date',
    noDateSelected: 'No date selected',
  },
};
